// registration-wizard/MemberSearch.tsx
import { useRef, useState } from 'react';
import { Combobox, useCombobox, Loader, Text } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useMemberService, Member } from '@actschurch/ccms-sdk';
import { TextInput } from '@/components';

export interface MemberSearchProps {
  searchTerm: string;
  onSearchChange: (val: string) => void;
  onSelect: (member: Member) => void;
}

export function MemberSearch({ searchTerm, onSearchChange, onSelect }: MemberSearchProps) {
  const memberService = useMemberService();
  const combobox      = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() });
  const [results, setResults] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef           = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); combobox.closeDropdown(); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const found = await memberService.searchMembers(query.trim(), 10);
        setResults(found ?? []);
        (found ?? []).length > 0 ? combobox.openDropdown() : combobox.closeDropdown();
      } catch {
        toast.error('Search Error', 'Could not search for members.');
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleChange = (val: string) => { onSearchChange(val); runSearch(val); };

  const handleSelect = (memberID: string) => {
    const member = results.find((m) => String(m.memberID) === memberID);
    if (member) { onSelect(member); setResults([]); combobox.closeDropdown(); }
  };

  return (
    <Combobox store={combobox} onOptionSubmit={handleSelect}>
      <Combobox.Target>
        <TextInput
          placeholder="Search by name or email…"
          leftSection={loading ? <Loader size={14} /> : <IconSearch size={14} />}
          value={searchTerm}
          onChange={(e) => handleChange(e.currentTarget.value)}
          onClick={() => results.length > 0 && combobox.openDropdown()}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options>
          {results.length === 0 ? (
            <Combobox.Empty>No members found</Combobox.Empty>
          ) : (
            results.map((m) => (
              <Combobox.Option key={m.memberID} value={String(m.memberID)}>
                <Text size="sm" fw={500}>
                  {[m.firstName, m.lastName].filter(Boolean).join(' ')}
                </Text>
                {m.email      && <Text size="xs" c="dimmed">{m.email}</Text>}
                {m.cellNumber && <Text size="xs" c="dimmed">{m.cellNumber}</Text>}
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

export default MemberSearch;